import React from 'react';

export const Field = ({ label, hint, children }) => (
  <div className="mb-5">
    <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
    {children}
  </div>
);

export const TextInput = ({ label, hint, value, onChange, placeholder }) => (
  <Field label={label} hint={hint}>
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
    />
  </Field>
);

export const TextArea = ({ label, hint, value, onChange, placeholder, rows = 3 }) => (
  <Field label={label} hint={hint}>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
    />
  </Field>
);

export const Toggle = ({ label, hint, value, onChange }) => (
  <Field label={label} hint={hint}>
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </Field>
);

// Edit a list of strings
export const StringList = ({ label, hint, value = [], onChange, placeholder = 'Item...' }) => {
  const update = (i, v) => { const a = [...value]; a[i] = v; onChange(a); };
  const add = () => onChange([...value, '']);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <Field label={label} hint={hint}>
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={e => update(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button onClick={() => remove(i)} className="text-gray-400 hover:text-red-500 transition-colors px-2">
              <span className="material-icons text-lg">close</span>
            </button>
          </div>
        ))}
        <button
          onClick={add}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          <span className="material-icons text-base">add</span> Adicionar item
        </button>
      </div>
    </Field>
  );
};

export const SectionHeader = ({ title }) => (
  <div className="border-b border-gray-100 pb-3 mb-6">
    <h3 className="text-base font-bold text-gray-800">{title}</h3>
  </div>
);

export const CardList = ({ label, value = [], onChange, renderCard, emptyCard }) => {
  const update = (i, patch) => {
    const a = [...value];
    a[i] = { ...a[i], ...patch };
    onChange(a);
  };
  const add = () => onChange([...value, { ...emptyCard }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const a = [...value];
    const j = i + dir;
    if (j < 0 || j >= a.length) return;
    [a[i], a[j]] = [a[j], a[i]];
    onChange(a);
  };

  return (
    <Field label={label}>
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-4 bg-gray-50 relative">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">#{i + 1}</span>
              <div className="flex gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1">
                  <span className="material-icons text-sm">arrow_upward</span>
                </button>
                <button onClick={() => move(i, 1)} disabled={i === value.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1">
                  <span className="material-icons text-sm">arrow_downward</span>
                </button>
                <button onClick={() => remove(i)} className="text-gray-400 hover:text-red-500 p-1">
                  <span className="material-icons text-sm">delete</span>
                </button>
              </div>
            </div>
            {renderCard(item, i, (patch) => update(i, patch))}
          </div>
        ))}
        <button
          onClick={add}
          className="w-full border-2 border-dashed border-gray-200 rounded-xl py-3 text-sm text-gray-400 hover:border-blue-300 hover:text-blue-500 transition-colors flex items-center justify-center gap-1"
        >
          <span className="material-icons text-base">add</span> Adicionar
        </button>
      </div>
    </Field>
  );
};
